import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { MAX_USER_GROUP_NAME_LENGTH } from "../../../../shared/mutators/createUserGroup";
import { namesMatch, normalizeName } from "../../../../shared/normalizeName";
import { queries } from "../../../../shared/queries";
import {
  type GrantEntry,
  type RoleOption,
  rolesForScope,
} from "../../../../shared/rbac/roles";
import { customRolesForScope } from "../../../lib/grantAssembly";
import { defaultRoleForScope } from "../../../lib/defaultRoleForScope";
import { useCurrentUserId } from "../../../hooks/useCurrentUserId";
import { useQuery } from "../../../hooks/useQuery";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { usePrincipalModals } from "../../../hooks/usePrincipalModals";
import { useScopeAccessCloseWarning } from "../../ScopeAccessCloseWarning";
import { ScopeAccessTable } from "../../ScopeAccessTable";
import {
  HeaderRight,
  SettingsModalBody,
  SettingsModalFooter,
  SettingsModalHeader,
} from "../../SettingsLayout";
import { Text } from "../../Text";
import {
  Card,
  ColoredIconBadge,
  FieldLabel,
  SectionHeading,
  settingsModalStyle,
} from "../shared";

type Assignment = {
  userId: string;
  roleOption: RoleOption;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CreateUserGroupModal({ open, onClose }: Props) {
  const z = useZero();
  const { onOpenUser } = usePrincipalModals();
  const currentUserId = useCurrentUserId();
  // When the IdP owns group membership ("jit_users_and_groups" or SCIM), the group can
  // be created but no members — not even the creator — may be assigned here.
  const { locked: membershipLocked, reason: membershipReason } =
    useIdpSyncLock("groupMembership");
  const [name, setName] = useState("");
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({ singular: "member", plural: "members" });

  const [allGroups] = useQuery(open ? queries.allUserGroupsLite() : undefined);
  const [allUsers] = useQuery(open ? queries.allUsers() : undefined);
  const [customRoles] = useQuery(open ? queries.allRolesLite() : undefined);

  const activeUsers = useMemo(
    () => (allUsers ?? []).filter((u) => !u.deactivated),
    [allUsers]
  );

  const groupCustomRoles = useMemo(
    () => customRolesForScope(customRoles ?? [], "user_group"),
    [customRoles]
  );

  const roleOptions: RoleOption[] = useMemo(
    () => rolesForScope("user_group", groupCustomRoles),
    [groupCustomRoles]
  );

  const defaultManagerRole = useMemo(
    () =>
      roleOptions.find((r) => r.builtinRoleId === "user_group_manager") ??
      roleOptions[0],
    [roleOptions]
  );

  const normalized = normalizeName(name);
  const nameTaken =
    normalized.length > 0 &&
    (allGroups ?? []).some((g) => namesMatch(g.name, normalized));

  useEffect(() => {
    if (open && defaultManagerRole) {
      setAssignments(
        membershipLocked
          ? []
          : [{ userId: currentUserId, roleOption: defaultManagerRole }]
      );
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, currentUserId, defaultManagerRole, membershipLocked]);

  const reset = () => {
    setName("");
    setAssignments([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const allGrants: GrantEntry[] = useMemo(
    () =>
      assignments.map((a) => ({
        userId: a.userId,
        builtinRoleId: a.roleOption.builtinRoleId,
        customRoleId: a.roleOption.customRoleId,
      })),
    [assignments]
  );

  const findRoleOption = useCallback(
    (g: GrantEntry): RoleOption | undefined => {
      if (g.builtinRoleId) {
        return roleOptions.find((o) => o.builtinRoleId === g.builtinRoleId);
      }
      if (g.customRoleId) {
        return roleOptions.find((o) => o.customRoleId === g.customRoleId);
      }
      return undefined;
    },
    [roleOptions]
  );

  const commitGrants = useCallback(
    (next: GrantEntry[]) => {
      setAssignments(
        next
          .filter((g): g is GrantEntry & { userId: string } => !!g.userId)
          .map((g) => {
            const role = findRoleOption(g) ?? roleOptions[0];
            return { userId: g.userId, roleOption: role! };
          })
      );
    },
    [findRoleOption, roleOptions]
  );

  const [confirmingSelfRemove, setConfirmingSelfRemove] = useState(false);

  const removeUser = useCallback(
    (userId: string) => {
      if (userId === currentUserId) {
        setConfirmingSelfRemove(true);
        return;
      }
      setAssignments((prev) => prev.filter((a) => a.userId !== userId));
    },
    [currentUserId]
  );

  const confirmRemoveSelf = useCallback(() => {
    setAssignments((prev) => prev.filter((a) => a.userId !== currentUserId));
    setConfirmingSelfRemove(false);
  }, [currentUserId]);

  const assignedUsers = useMemo(
    () => activeUsers.filter((u) => assignments.some((a) => a.userId === u.id)),
    [activeUsers, assignments]
  );

  const handleCreate = () => {
    if (nameTaken || !normalized) return;

    const grants = assignments.map((a) => ({
      userId: a.userId,
      builtinRoleId: a.roleOption.builtinRoleId,
      customRoleId: a.roleOption.customRoleId,
    }));

    z.mutate(
      sharedMutators.createUserGroup({
        id: nanoid(12),
        name: normalized,
        grants,
      })
    );
    reset();
    onClose();
  };

  const close = () => requestClose(handleClose);
  const create = () => requestClose(handleCreate);

  return (
    <>
      <Modal open={open} onClose={close} style={settingsModalStyle}>
        <SettingsModalHeader>
          <ColoredIconBadge id="user_group" iconName="users-round" />
          <Text variant="text-regular-md">Create group</Text>
          <HeaderRight>
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </HeaderRight>
        </SettingsModalHeader>

        <SettingsModalBody style={{ gap: 24 }} data-popover-host>
          <Card>
            <FieldLabel>
              <Box horizontal={8} bottom={10}>
                <Text variant="text-small-md">Group name</Text>
              </Box>
              <Input
                ref={inputRef}
                placeholder="e.g. Designers"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={MAX_USER_GROUP_NAME_LENGTH}
                style={{ width: "100%" }}
              />
              {nameTaken && (
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-danger)">
                    A group with this name already exists.
                  </Text>
                </Box>
              )}
            </FieldLabel>
          </Card>

          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Members</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Add members and choose their role.
              </Text>
            </SectionHeading>
            <ScopeAccessTable
              allGrants={allGrants}
              assignedGroups={[]}
              assignedUsers={assignedUsers}
              roleOptions={roleOptions}
              allGroups={[]}
              allUsers={activeUsers}
              customRoles={groupCustomRoles}
              canManage
              compact
              commitGrants={commitGrants}
              removeGroup={() => {}}
              removeUser={removeUser}
              memberRoster
              onOpenUser={onOpenUser}
              idpSyncLocked={membershipLocked}
              idpSyncReason={membershipReason}
              stagedGrantsRef={stagedGrantsRef}
              defaultRole={defaultRoleForScope("user_group", roleOptions)}
            />
          </Card>
        </SettingsModalBody>

        <SettingsModalFooter style={{ gap: 8 }}>
          <Button size="l" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button size="l" onClick={create} disabled={!normalized || nameTaken}>
            Create
          </Button>
        </SettingsModalFooter>

        <ConfirmModal
          open={confirmingSelfRemove}
          onClose={() => setConfirmingSelfRemove(false)}
          onConfirm={confirmRemoveSelf}
          title="Remove yourself?"
          description="You won’t have access to this group once it’s created."
          confirmLabel="Remove"
          destructive
        />
      </Modal>
      {closeWarningModal}
    </>
  );
}
